import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import GlossaryCard from 'components/GlossaryCard';
import FilterBar from 'components/FilterBar';
import Pagination from 'components/Pagination';
import { parseDoubleEscapedJson } from 'utils/fixDoubleEscapedJSON.js';
import { useLocation, useNavigate } from 'react-router-dom';

const ITEMS_PER_PAGE = 10;

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

export default function SearchView(longidObj) {

  const longid = longidObj.longid;

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [searchHappened, setSearchHappened] = useState(false);

  const [mode, setMode] = useState('idle'); // 'idle' | 'suggest' | 'single' | 'results'
  const [selectedItem, setSelectedItem] = useState(null);

  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [filterSearch, setFilterSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [sortBy, setSortBy] = useState('');
  const [tag, setTag] = useState('all');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [autoExpandedId, setAutoExpandedId] = useState(null);

  const queryParams = useQueryParams();
  const directId = queryParams.get('id');

  const autoController = useRef(null);
  const singleController = useRef(null);
  const fullSearchController = useRef(null);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);


const hash = window.location.hash;
const uidMatch = hash.match(/uid=([^&]+)/);
const directUid = uidMatch ? uidMatch[1] : null;

const navigate = useNavigate();

const location = useLocation();


// check for glossary search
  useEffect(() => {
    const searchTerms = location.search;
    if (!searchTerms.includes('?glossary=')) return;
    const term = decodeURIComponent(searchTerms.split('?glossary=')[1].split('/')[0] || '').trim();
    if (!term) return;

    // use AbortController to cancel the request if effect runs again / component unmounts
    const controller = new AbortController();

    setSearchHappened(true);
    setLoading(true);

    (async () => {
      try {
        const url = longid ? `/edgconnect/term/glossary/${term}?longid=${longid}` : `/edgconnect/term/glossary/${term}?longid=90fa602c`
        const res = await axios.get(url, { signal: controller.signal });
        if (res.data !== `{"response":"false"}`) {
          let glossaryResults = parseDoubleEscapedJson(res.data);
          setResults(glossaryResults.pageItems);
          setShowSuggestions(false);
          setSelectedItem(null);
          setLoading(false);
          setHighlightedIndex(-1);
          setMode('results');
          setCurrentPage(1);
          setFilterSearch('');
          setRiskFilter('all');
          setSortBy('');
          setTag('all');
        }
        else console.log('no data (response: false), returning')
        return;
      } catch (err) {
        console.error('Glossary API error:', err);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [location]);

  // Direct ID on page load
  useEffect(() => {
    const idToLoad = directId || directUid;
    if (!idToLoad) return;

    if (singleController.current) singleController.current.abort();
    const controller = new AbortController();
    singleController.current = controller;
    setLoading(true);
    async function fetchById() {
      try {
        const url = longid ? `/edgconnect/term/id/${idToLoad}?longid=${longid}` : `/edgconnect/term/id/${idToLoad}?longid=90fa602c`
        const res = await axios.get(url, {
          signal: controller.signal,
        });
        const parsed = parseDoubleEscapedJson(res.data);
        const termObject = parsed.pageItems?.[0] || parsed;

        setSelectedItem(termObject);
        setFilteredResults([termObject]);
        setMode('single');
        setShowSuggestions(false);
        setAutoExpandedId(directId);
        filteredResults && setLoading(false);
      } catch (err) {
        if (!axios.isCancel(err)) console.error(err);
      }
    }

    fetchById();

    return () => controller.abort();
  }, [directId]);

const isAcronym = (str) => /^[A-Z0-9]{2,}$/.test(str || '');

  // Autocomplete search
  useEffect(() => {
    if (!inputRef.current?.matches(':focus')) return;
    if (query.length > 1) setShowSuggestions(true);
     if (!query && query.length == 0) {
      setShowSuggestions(false);
      return;
    }

    if (autoController.current) autoController.current.abort();
    const controller = new AbortController();
    autoController.current = controller;

    const debounce = setTimeout(async () => {
      try {
        const url = longid ? `/edgconnect/term/auto/${query}?longid=${longid}` : `/edgconnect/term/auto/${query}?longid=90fa602c`
        const res = await axios.get(url, {
          signal: controller.signal,
        });
        const data = parseDoubleEscapedJson(res.data);
        const items = (data.pageItems || [])
          .filter((item) => isAcronym(item.term || item.name))
          .slice(0, 10);
        setSuggestions(items);
        setShowSuggestions(items.length > 0);
        if (mode !== 'single' && mode !== 'results') setMode('suggest');
        setHighlightedIndex(-1);
      } catch (err) {
        if (!axios.isCancel(err)) console.error(err);
      }
    }, 100);

    return () => {
      clearTimeout(debounce);
      controller.abort();
    };
  }, [query]);

  // Dropdown selection
  const handleSelectSuggestion = async (item) => {
    setShowSuggestions(false);
    setSearchHappened(true);
    if (autoController.current) autoController.current.abort();
    if (singleController.current) singleController.current.abort();
    if (fullSearchController.current) fullSearchController.current.abort();

    setSearchHappened(true);
    setLoading(true);
    setQuery(item.term || item.name || '');
    setError(null);
    inputRef.current?.blur();

    navigate(`/acronyms?uid=${item.id}`, { replace: true });

    const controller = new AbortController();
    singleController.current = controller;

    try {
      const url = longid ? `/edgconnect/term/id/${item.id}?longid=${longid}` : `/edgconnect/term/id/${item.id}?longid=90fa602c`
      const res = await axios.get(url, {
        signal: controller.signal,
      });
      setShowSuggestions(false);
      const data = parseDoubleEscapedJson(res.data);
      const termObject = data.pageItems?.[0] || data;
      setSelectedItem(termObject);
      setFilteredResults([termObject]);
      setMode('single');
      setAutoExpandedId(item.id);
      setLoading(false);
    } catch (err) {
      if (!axios.isCancel(err)) console.error(err);
      else setError('Failed to load item details.');

    }
  };

 // Full search
const handleFullSearch = async (e) => {
  e?.preventDefault();
  autoController.current?.abort();
  singleController.current?.abort();
  fullSearchController.current?.abort();
  setSearchHappened(true);
  setResults([]);
  setLoading(true);
  setError(null);
  setSelectedItem(null);
  setShowSuggestions(false);
  setHighlightedIndex(-1);
  setMode('results');
  navigate(`/acronyms`);
  const controller = new AbortController();
  fullSearchController.current = controller;

  try {
    const url = longid ? `/edgconnect/term/search/${query}?longid=${longid}` : `/edgconnect/term/search/${query}?longid=90fa602c`
    const res = await axios.get(url, {
      signal: controller.signal,
    });

    const dataFullSearch = parseDoubleEscapedJson(res.data);

     const onlyAcronyms = (dataFullSearch.pageItems || []).filter((item) =>
        isAcronym(item.name || item.term)
      );


    // Display actual results
    setResults(onlyAcronyms || []);

    setCurrentPage(1);
    setFilterSearch('');
    setRiskFilter('all');
    setSortBy('');
    setTag('all');
    filteredResults && setLoading(false);
  } catch (err) {
    if (!axios.isCancel(err)) {
      console.error(err);
      setError('Search failed. Please try again.');
    }
  }
};

  // Filtering & sorting
  useEffect(() => {
    if (mode !== 'results') return;

    let filtered = [...results];

    if (filterSearch) {
      filtered = filtered.filter((item) =>
        (item.name || '').toLowerCase().includes(filterSearch.toLowerCase())
      );
    }

    if (riskFilter !== 'all') {
      filtered = filtered.filter((item) =>
        (item.sensitivity || []).some((val) => val.toLowerCase() === riskFilter.toLowerCase())
      );
    }

    if (tag !== 'all') {
      filtered = filtered.filter((item) => (item.tags || []).includes(tag));
    }

    if (sortBy === 'alphabetical') {
      filtered.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
    } else if (sortBy === 'lastUpdated') {
      filtered.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
    }

    setFilteredResults(filtered);
    setCurrentPage(1);
  }, [results, filterSearch, riskFilter, sortBy, tag, mode]);

  // Tag options
  const tagOptions = useMemo(() => {
    const setTags = new Set();
    results.forEach((item) => (item.tags || []).forEach((t) => setTags.add(t)));
    return Array.from(setTags).sort();
  }, [results]);

  // Pagination
  const paginationItems = useMemo(() => {
    if (!Array.isArray(filteredResults)) return [];
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredResults.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredResults, currentPage]);

  const totalResults = Array.isArray(filteredResults) ? filteredResults.length : 0;
  const startResult = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endResult = Math.min(currentPage * ITEMS_PER_PAGE, totalResults);

  // Handle clicks outside search element
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !inputRef.current?.contains(event.target) &&
        !suggestionsRef.current?.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev <= 0 ? suggestions.length - 1 : prev - 1
      );
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0) {
        e.preventDefault();
        handleSelectSuggestion(suggestions[highlightedIndex]);
      } else {
        handleFullSearch();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const escapeRegExp = (str) =>
  str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const handleClear = function() {
    navigate(`/acronyms`);
    setSearchHappened(false);
    setSelectedItem(null);
    setFilteredResults([]);
    setMode('idle');
    setSuggestions([]);
    setShowSuggestions(false);
    setResults([]);
    setQuery("");
  }

  return (
    <div>
      {/* SEARCH INPUT */}
      <div className="p-4 mb-4 border rounded-b-lg rounded-tr-lg shadow-md border-black/10 bg-grey-50">
        <form onSubmit={handleFullSearch} className="relative">
          <div className="flex shadow-sm">
            <div className="relative w-full">
              <input
                ref={inputRef}
                type="text"
                placeholder="Search titles by keyword..."
                className="w-full py-2 pl-2 pr-8 border rounded-l border-grey-200"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (mode !== 'suggest' && mode !== 'results') setMode('idle');  // Reset state
                }}
                onKeyDown={handleKeyDown}
                onFocus={() => query && setShowSuggestions(true)}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => handleClear()}
                  className="absolute px-2 py-1 text-white rounded clear-btn right-1 top-[5px] bg-ubc-blue hover--opacity-80"
                >
                  Clear
                </button>
              )}
            </div>
            <button
              type="submit"
              className="px-5 text-sm text-white bg-ubc-blue rounded-r-md hover--opacity-80"
            >
              SEARCH
            </button>
          </div>

          {/* SUGGESTIONS */}
          {showSuggestions && suggestions.length > 0 && (
            <ul
              ref={suggestionsRef}
              className="absolute z-10 w-full p-0 m-0 overflow-y-auto list-none bg-white rounded shadow max-h-96"
            >
              {suggestions.map((sug, idx) => (
                <li
                  key={idx}
                  onClick={() => handleSelectSuggestion(sug)}
                  className={`px-2 py-1 m-0 border-b cursor-pointer border-light-blue hover--bg-ubc-blue hover--text-white ${
                    idx === highlightedIndex ? 'bg-ubc-blue text-white' : ''
                  }`}
                >
                  <span
                    className="w-full text-left"
                    dangerouslySetInnerHTML={{
                      __html: (sug.term || sug.name || '')
                      .replace(
                        new RegExp(`(${escapeRegExp(query)})`, 'gi'),
                        '<strong>$1</strong>'
                      ),
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </form>
      </div>

      {/* RESULTS AREA */}
      <div className="p-4 mb-4 border rounded-lg shadow-md border-black/10 bg-grey-50">
        {loading && <p className="my-3 text-center">
            <svg className="inline-block w-[100px] h-[100px]" alt="loading..." width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" >
              <circle cx="50" cy="50" r="31.8736" fill="none" stroke="#8cd0e5" stroke-width="1">
                <animate attributeName="r" calcMode="spline" values="0;40" keyTimes="0;1" dur="1" keySplines="0 0.2 0.8 1" begin="-0.5s" repeatCount="indefinite"></animate>
                <animate attributeName="opacity" calcMode="spline" values="1;0" keyTimes="0;1" dur="1" keySplines="0.2 0 0.8 1" begin="-0.5s" repeatCount="indefinite"></animate>
              </circle>
              <circle cx="50" cy="50" r="11.6081" fill="none" stroke="#002145" stroke-width="1">
                <animate attributeName="r" calcMode="spline" values="0;40" keyTimes="0;1" dur="1" keySplines="0 0.2 0.8 1" begin="0s" repeatCount="indefinite"></animate>
                <animate attributeName="opacity" calcMode="spline" values="1;0" keyTimes="0;1" dur="1" keySplines="0.2 0 0.8 1" begin="0s" repeatCount="indefinite"></animate>
              </circle>
            </svg>
          </p>}
        {error && <p className="my-3 text-sm text-high-risk">{error}</p>}

        {!results.length > 0 && !loading && !searchHappened && (
          <p className="my-3 text-muted">Please enter some terms to begin your search.</p>
        )}

        {selectedItem && !loading && (
          <div className="mt-6">
            <GlossaryCard
              term={selectedItem}
              autoExpand={selectedItem.id === autoExpandedId}
            />
            {totalResults > 0 && searchHappened && (
              <div className="my-3 text-sm text-muted">
                {totalResults > 0
                  ? `Viewing 1 of 1 results`
                  : ''}
              </div>
            )}
          </div>


        )}

        {mode === 'results' && !loading && (
          <div>
          {results.length > 0 && (
            <FilterBar
              search={filterSearch}
              onSearch={setFilterSearch}
              riskFilter={riskFilter}
              onRiskFilter={setRiskFilter}
              sortBy={sortBy}
              onSortBy={setSortBy}
              tag={tag}
              onTag={setTag}
              tagOptions={tagOptions}
            />)}

            <Pagination
              total={totalResults}
              perPage={ITEMS_PER_PAGE}
              current={currentPage}
              onChange={setCurrentPage}
            />

            <div className="mt-4">
              {paginationItems.length === 0 ? (
                <div className="text-muted">No Results. Please adjust your terms and try another search or <a href="#/a-z">browse our library</a>.</div>
              ) : (
                paginationItems.map((term) => (
                  <GlossaryCard
                    key={term.id}
                    term={term}
                    autoExpand={term.id === autoExpandedId}
                  />
                ))
              )}
            </div>

            <Pagination
              total={totalResults}
              perPage={ITEMS_PER_PAGE}
              current={currentPage}
              onChange={setCurrentPage}
            />

            {totalResults > 0 && searchHappened && (
              <div className="my-3 text-sm text-muted">
                {totalResults > 0
                  ? `Viewing ${startResult}-${endResult} of ${totalResults} results`
                  : ''}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
